"use client";

import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Icon } from "@/components/ui/icon";

const inputVariants = cva(
  cn(
    "flex w-full min-w-0 text-heading outline-none items-center transition-all duration-200",
    "placeholder:text-contrast/20",
    "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
    "data-[disabled=true]:opacity-50 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:pointer-events-none",
    "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium",
    "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&[type=number]]:[-moz-appearance:textfield]",
    // Native time picker duplicates surrounding UI; trim reserved padding inside the WebKit time editor.
    "[&[type=time]]:pr-0",
    "[&[type=time]::-webkit-calendar-picker-indicator]:hidden",
    "[&[type=time]::-webkit-datetime-edit]:min-w-0 [&[type=time]::-webkit-datetime-edit]:p-0",
    "[&[type=time]::-webkit-datetime-edit-fields-wrapper]:p-0",
  ),
  {
    variants: {
      variant: {
        none: "",
        muted:
          "bg-contrast/3 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-contrast/30 focus-within:border-contrast/30",
        default:
          "bg-contrast/6 border border-contrast/[0.15] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-contrast/30 focus-within:border-contrast/30",
        auth: "bg-secondary border placeholder:text-contrast/20 focus:ring-1 focus:ring-contrast/20",
        filter:
          "bg-contrast/3 border border-contrast/15 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
        modal:
          "bg-contrast/[0.02] border border-contrast/15 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-primary focus-within:border-primary",
        project:
          "bg-contrast/3 border border-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus:border-contrast/20 focus-within:border-contrast/20",
        toolbar: "bg-contrast/3 border border-border",
        ghost: "bg-transparent",
        settings:
          "bg-contrast/6 border border-contrast/[0.15 overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
        "settings-active":
          "bg-contrast/[0.02] border border-primary overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
        readonly:
          "bg-contrast/3 border border-contrast/5 text-contrast/50 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
      },
      size: {
        none: "",
        xs: "h-7 px-2.5 text-xs gap-1.5 rounded-sm",
        sm: "h-8 px-3 text-sm gap-2 rounded-sm",
        default: "h-9 py-2 px-3 text-sm gap-2 rounded-md",
        lg: "h-10 px-4 text-sm gap-2  rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type InputVariantProps = VariantProps<typeof inputVariants>;

export interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    InputVariantProps {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, variant, size, type, ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      type={type}
      data-slot="input"
      className={cn(inputVariants({ variant, size }), className)}
      {...props}
    />
  );

  return input;
});

function PasswordInput({
  className,
  variant = "auth",
  size = "default",
  ...props
}: Omit<InputProps, "type">) {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <div className="relative">
      <input
        {...props}
        type={showPassword ? "text" : "password"}
        data-slot="input"
        className={cn(inputVariants({ variant, size }), "pr-10", className)}
      />
      <button
        type="button"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-contrast/40 hover:text-contrast/60 transition-colors flex items-center justify-center size-5"
        onClick={() => setShowPassword((prev) => !prev)}
        tabIndex={-1}
        aria-label={showPassword ? "Hide password" : "Show password"}
      >
        <Icon name={showPassword ? "IconEyeClosed" : "IconEyeOpen"} size={16} />
      </button>
    </div>
  );
}

interface SearchInputProps extends Omit<InputProps, "type"> {
  iconSize?: number;
}

function SearchInput({
  className,
  variant = "toolbar",
  size = "xs",
  onChange,
  value,
  iconSize = 16,
  disabled,
  ...props
}: SearchInputProps) {
  return (
    <div
      className={cn(
        inputVariants({
          variant,
          size,
          className: "flex shrink-0 items-center font-normal",
        }),
        className,
      )}
      data-disabled={disabled || undefined}
    >
      <Icon
        name="IconMagnifyingGlass"
        size={iconSize}
        className="text-heading opacity-30 shrink-0"
      />
      <input
        type="text"
        data-slot="input"
        disabled={disabled}
        className="flex-1 bg-transparent text-inherit placeholder:text-contrast/30 outline-none min-w-0"
        onChange={onChange}
        value={value}
        {...props}
      />
    </div>
  );
}

interface ContainerInputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    InputVariantProps {
  inputClassName?: string;
  children?: React.ReactNode;
}

function ContainerInput({
  className,
  inputClassName,
  variant = "default",
  size = "default",
  onChange,
  value,
  children,
  disabled,
  ...props
}: ContainerInputProps) {
  return (
    <div
      className={cn(
        inputVariants({
          variant,
          size,
          className: "relative flex items-center gap-2 font-normal",
        }),
        className,
      )}
      data-disabled={disabled || undefined}
    >
      <input
        type="text"
        data-slot="input"
        disabled={disabled}
        className={cn(
          "flex-1 bg-transparent text-heading placeholder:text-contrast/50 outline-none min-w-0",
          inputClassName,
        )}
        onChange={onChange}
        value={value}
        {...props}
      />
      {children}
    </div>
  );
}

export interface TextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    InputVariantProps {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, variant, size, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        data-slot="textarea"
        className={cn(
          inputVariants({ variant, size: "none" }),
          "py-2 px-3 text-sm rounded-md",
          className,
        )}
        {...props}
      />
    );
  },
);

export {
  ContainerInput,
  Input,
  inputVariants,
  PasswordInput,
  SearchInput,
  Textarea,
};
